import React, { Suspense, useContext, useMemo } from 'react'
import { Redirect } from 'wouter'
import { ROLES } from '../constants'
import { SubjectContextProvider } from '../context/SubjectContext'
import { User, UserContext } from '../context/UserContext'
import { WizardContextProvider } from '../context/WizardContext'
import useRoutes, { RouteType } from '../hooks/useRoutes'
import { EnrollContainerLogged, EnrollContainerNotLogged } from '../pages/Enroll/EnrollContainer'
import LoadingPage from '../pages/Other/LoadingPage'
import { FillInfoContextProvider } from '../pages/Signup/Wizard/FillInfoContext'
import { TeamContextProvider } from '../pages/Team/Context/TeamContext'

// Convert heavy pages to lazy-loaded chunks
const AddStudentContainer = React.lazy(() => import('../pages/Teams/AddStudent/AddStudentContainer'))
const Auth0Callback = React.lazy(() => import('../pages/Login/Auth0/Auth0Callback'))
const Dashboard = React.lazy(() => import('../pages/Teams/Dashboard/Dashboard').then((m) => ({ default: m.Dashboard })))
const DataRevisionContainer = React.lazy(() => import('../pages/Students/Import/DataRevisionContainer'))
const EmailVerifyContainer = React.lazy(() => import('../pages/Verify/VerifyContainer'))
const LoginContainer = React.lazy(() => import('../pages/Login/LoginContainer'))
const LoginWithIdToken = React.lazy(() =>
    import('../pages/Login/LoginWithIdToken').then((m) => ({ default: m.LoginWithIdToken }))
)
const NotFoundPage = React.lazy(() => import('../pages/Other/NotFoundPage'))
const NotificationContainer = React.lazy(() => import('../pages/NotificationCenter/NotificationContainer'))
const Overview = React.lazy(() => import('../pages/Team/Overview').then((m) => ({ default: m.Overview })))
const PasswordManageContainer = React.lazy(() => import('../pages/Password/PasswordManageContainer'))
const ProfilePageContainer = React.lazy(() => import('../pages/Profile/ProfilePageContainer'))
const ReportsPDF = React.lazy(() =>
    import('../pages/Team/Reports/Components/ReportsPDF').then((m) => ({ default: m.ReportsPDF }))
)
const Settings = React.lazy(() => import('../pages/Team/Settings').then((m) => ({ default: m.Settings })))
const ShareContainer = React.lazy(() => import('../pages/Share/ShareContainer'))
const SignupCheckingView = React.lazy(() => import('../pages/Signup/SignupCheckinView'))
const SignupContainer = React.lazy(() => import('../pages/Signup/SignupContainer'))
const SignupFillInfoContainer = React.lazy(() => import('../pages/Signup/Wizard/SignupFillInfoContainer'))
const StudentDetailContainer = React.lazy(() => import('../pages/Students/Detail/StudentDetailContainer'))
const StudentImportContainer = React.lazy(() => import('../pages/Students/Import/StudentImportContainer'))
const Team = React.lazy(() => import('../pages/Team/Team').then((m) => ({ default: m.Team })))
const TeamDetailContainer = React.lazy(() => import('../pages/Teams/Detail/TeamDetailContainer'))
const TeamManagerContainer = React.lazy(() => import('../pages/Teams/Manage/TeamManagerContainer'))
const TeamProfileContainer = React.lazy(() => import('../pages/Teams/Profile/TeamProfileContainer'))
const TeamSettingsContainer = React.lazy(() => import('../pages/Teams/Settings/TeamSettingsContainer'))
const TeamsCreateContainer = React.lazy(() => import('../pages/Teams/Dashboard/TeamsCreateContainer'))
const TermsAndConditions = React.lazy(() => import('../pages/Signup/TermsAndConditions'))
const WizardContainer = React.lazy(() => import('../pages/Wizard/WizardContainer'))
const Reports = React.lazy(() => import('../pages/Team/Reports'))
const CohortsDashboard = React.lazy(() => import('../pages/Network/CohortsDashboard/CohortsDashboard'))
const UnifiedReports = React.lazy(() => import('../pages/Network/UnifiedReports/UnifiedReports'))
const UnifiedReportsPDF = React.lazy(() => import('../pages/Network/UnifiedReports/UnifiedReportsPDF'))

const AppRouter = () => {
    const { user, role, skipValidation, logged, isRecoveringSession } = useContext(UserContext)

    const routes = useMemo(() => {
        return user ? privateRoutes : publicRoutes
    }, [user])

    const [match] = useRoutes(routes)

    // Esperar a que termine la recuperación de sesión antes de decidir rutas
    if (isRecoveringSession) {
        return <LoadingPage />
    }

    if (user && !skipValidation) {
        if (user.emailVerified === false) {
            return (
                <Suspense fallback={<LoadingPage />}>
                    <EmailVerifyContainer />
                </Suspense>
            )
        } else if (shouldCompleteProfile(user, role)) {
            return (
                <FillInfoContextProvider>
                    <Suspense fallback={<LoadingPage />}>
                        <SignupFillInfoContainer />
                    </Suspense>
                </FillInfoContextProvider>
            )
        }
    }

    return (
        <Suspense fallback={<LoadingPage />}>
            {logged ? match || <NotFoundPage /> : match || <LoginContainer />}
        </Suspense>
    )
}

const TeamRouter = () => {
    const routesByRol: { [role in ROLES]: RouteType } = useMemo(
        () => ({
            [ROLES.SUPERADMIN]: {},
            [ROLES.ADMIN]: {
                '/': () => (
                    <Suspense fallback={<LoadingPage />}>
                        <TeamDetailContainer />
                    </Suspense>
                ),
                '/manager': [
                    () => (
                        <Suspense fallback={<LoadingPage />}>
                            <TeamManagerContainer />
                        </Suspense>
                    ),
                    {
                        '/add': () => (
                            <Suspense fallback={<LoadingPage />}>
                                <AddStudentContainer />
                            </Suspense>
                        ),
                    },
                ],
                '/settings': () => (
                    <Suspense fallback={<LoadingPage />}>
                        <TeamSettingsContainer />
                    </Suspense>
                ),
                '/profile/:profile': ({ profile }) => (
                    <Suspense fallback={<LoadingPage />}>
                        <TeamProfileContainer profile={profile} />
                    </Suspense>
                ),
                '/import': [
                    () => (
                        <Suspense fallback={<LoadingPage />}>
                            <StudentImportContainer />
                        </Suspense>
                    ),
                    {
                        '/check': () => (
                            <Suspense fallback={<LoadingPage />}>
                                <DataRevisionContainer />
                            </Suspense>
                        ),
                    },
                ],
                // '/wizard': () => <ShareContainer />,
            },
            [ROLES.TEMPORAL]: {
                '/': () => (
                    <Suspense fallback={<LoadingPage />}>
                        <TeamDetailContainer />
                    </Suspense>
                ),
                '/manager': () => (
                    <Suspense fallback={<LoadingPage />}>
                        <TeamManagerContainer />
                    </Suspense>
                ),
                '/settings': () => (
                    <Suspense fallback={<LoadingPage />}>
                        <TeamSettingsContainer />
                    </Suspense>
                ),
                '/profile/:profile': ({ profile }) => (
                    <Suspense fallback={<LoadingPage />}>
                        <TeamProfileContainer profile={profile} />
                    </Suspense>
                ),
                '/import': [
                    () => (
                        <Suspense fallback={<LoadingPage />}>
                            <StudentImportContainer />
                        </Suspense>
                    ),
                    {
                        '/check': () => (
                            <Suspense fallback={<LoadingPage />}>
                                <DataRevisionContainer />
                            </Suspense>
                        ),
                    },
                ],
                '/wizard': () => (
                    <Suspense fallback={<LoadingPage />}>
                        <ShareContainer />
                    </Suspense>
                ),
            },
            [ROLES.COACH]: {
                '/': () => (
                    <Suspense fallback={<LoadingPage />}>
                        <TeamDetailContainer />
                    </Suspense>
                ),
                '/manager': [
                    () => (
                        <Suspense fallback={<LoadingPage />}>
                            <TeamManagerContainer />
                        </Suspense>
                    ),
                    {
                        '/add': () => (
                            <Suspense fallback={<LoadingPage />}>
                                <AddStudentContainer />
                            </Suspense>
                        ),
                    },
                ],
                '/settings': () => (
                    <Suspense fallback={<LoadingPage />}>
                        <TeamSettingsContainer />
                    </Suspense>
                ),
                '/import': [
                    () => (
                        <Suspense fallback={<LoadingPage />}>
                            <StudentImportContainer />
                        </Suspense>
                    ),
                    {
                        '/check': () => (
                            <Suspense fallback={<LoadingPage />}>
                                <DataRevisionContainer />
                            </Suspense>
                        ),
                    },
                ],
                '/wizard': () => (
                    <Suspense fallback={<LoadingPage />}>
                        <ShareContainer />
                    </Suspense>
                ),
                '/profile/:profile': ({ profile }) => (
                    <Suspense fallback={<LoadingPage />}>
                        <TeamProfileContainer profile={profile} />
                    </Suspense>
                ),
            },
            [ROLES.PROFESSOR]: {
                '/': () => (
                    <Suspense fallback={<LoadingPage />}>
                        <TeamDetailContainer />
                    </Suspense>
                ),
                '/manager': () => (
                    <Suspense fallback={<LoadingPage />}>
                        <TeamManagerContainer />
                    </Suspense>
                ),
                '/profile/:profile': ({ profile }) => (
                    <Suspense fallback={<LoadingPage />}>
                        <TeamProfileContainer profile={profile} />
                    </Suspense>
                ),
            },
            [ROLES.STUDENT]: {},
        }),
        []
    )
    const { role } = useContext(UserContext)
    const [match] = useRoutes(role ? routesByRol[role] : {}, '/team/:ps/subject/:s')
    if (match === undefined || !role) return <LoadingPage />
    return <Suspense fallback={<LoadingPage />}>{match || <NotFoundPage />}</Suspense>
}

const TeamRouterAndContext = ({ ps, s }: { ps: string; s: string }) => {
    return (
        <SubjectContextProvider professorSchedule={ps} subject={s}>
            <Suspense fallback={<LoadingPage />}>
                <TeamRouter />
            </Suspense>
        </SubjectContextProvider>
    )
}

/** All the PrivateRoutes accesible to every user */
const privateRoutes: RouteType = {
    '/': '/dashboard',
    '/dashboard': () => (
        <Suspense fallback={<LoadingPage />}>
            <Dashboard />
        </Suspense>
    ),
    // ===== NEW HIERARCHICAL ROUTES =====
    '/network/:networkId/cohorts': ({ networkId }) => (
        <Suspense fallback={<LoadingPage />}>
            <CohortsDashboard networkId={networkId} />
        </Suspense>
    ),
    '/network/:networkId/cohorts/:cohortId/overview': ({ networkId, cohortId }) => (
        <TeamContextProvider networkId={networkId} courseID={cohortId}>
            <Suspense fallback={<LoadingPage />}>
                <Overview />
            </Suspense>
        </TeamContextProvider>
    ),
    '/network/:networkId/cohorts/:cohortId/team': ({ networkId, cohortId }) => (
        <TeamContextProvider networkId={networkId} courseID={cohortId}>
            <Suspense fallback={<LoadingPage />}>
                <Team />
            </Suspense>
        </TeamContextProvider>
    ),
    '/network/:networkId/cohorts/:cohortId/reports': ({ networkId, cohortId }) => (
        <TeamContextProvider networkId={networkId} courseID={cohortId}>
            <Suspense fallback={<LoadingPage />}>
                <Reports />
            </Suspense>
        </TeamContextProvider>
    ),
    '/network/:networkId/cohorts/:cohortId/reports/pdf': ({ networkId, cohortId }) => (
        <TeamContextProvider networkId={networkId} courseID={cohortId}>
            <Suspense fallback={<LoadingPage />}>
                <ReportsPDF />
            </Suspense>
        </TeamContextProvider>
    ),
    '/network/:networkId/cohorts/:cohortId/settings': ({ networkId, cohortId }) => (
        <TeamContextProvider networkId={networkId} courseID={cohortId}>
            <Suspense fallback={<LoadingPage />}>
                <Settings />
            </Suspense>
        </TeamContextProvider>
    ),
    '/network/:networkId/reports': ({ networkId }) => (
        <Suspense fallback={<LoadingPage />}>
            <UnifiedReports networkId={networkId} />
        </Suspense>
    ),
    '/network/:networkId/reports/pdf': ({ networkId }) => (
        <Suspense fallback={<LoadingPage />}>
            <UnifiedReportsPDF networkId={networkId} />
        </Suspense>
    ),
    // ===== LEGACY ROUTES (for backwards compatibility) =====
    '/admin/team/:id': ({ id }) => (
        <TeamContextProvider courseID={id}>
            <Suspense fallback={<LoadingPage />}>
                <Team />
            </Suspense>
        </TeamContextProvider>
    ),
    '/admin/overview/:id': ({ id }) => (
        <TeamContextProvider courseID={id}>
            <Suspense fallback={<LoadingPage />}>
                <Overview />
            </Suspense>
        </TeamContextProvider>
    ),
    '/admin/settings/:id': ({ id }) => (
        <TeamContextProvider courseID={id}>
            <Suspense fallback={<LoadingPage />}>
                <Settings />
            </Suspense>
        </TeamContextProvider>
    ),
    '/admin/reports/:id': ({ id }) => (
        <TeamContextProvider courseID={id}>
            <Suspense fallback={<LoadingPage />}>
                <Reports />
            </Suspense>
        </TeamContextProvider>
    ),
    '/admin/reports/:id/pdf': ({ id }) => (
        <TeamContextProvider courseID={id}>
            <Suspense fallback={<LoadingPage />}>
                <ReportsPDF />
            </Suspense>
        </TeamContextProvider>
    ),
    '/team/:ps/subject/:s/:rest*': ({ ps, s }) => (
        <Suspense fallback={<LoadingPage />}>
            <TeamRouterAndContext ps={ps} s={s} />
        </Suspense>
    ),
    '/profile': [
        () => (
            <Suspense fallback={<LoadingPage />}>
                <ProfilePageContainer />
            </Suspense>
        ),
        {
            '/:profile/from/:professorSchedule/subject/:subject': (props) => (
                <Suspense fallback={<LoadingPage />}>
                    <StudentDetailContainer {...props} />
                </Suspense>
            ),
        },
    ],
    '/login': [
        '/dashboard',
        {
            '/verify': () => (
                <Suspense fallback={<LoadingPage />}>
                    <EmailVerifyContainer />
                </Suspense>
            ),
            '/superadmin': () => (
                <Suspense fallback={<LoadingPage />}>
                    <LoginWithIdToken />
                </Suspense>
            ),
        },
    ],
    '/signup': {
        '/verify': '/dashboard',
        '/complete': () => (
            <FillInfoContextProvider>
                <Suspense fallback={<LoadingPage />}>
                    <SignupFillInfoContainer />
                </Suspense>
            </FillInfoContextProvider>
        ),
        '/privacy': () => (
            <Suspense fallback={<LoadingPage />}>
                <TermsAndConditions />
            </Suspense>
        ),
    },
    '/welcome': () => (
        <Suspense fallback={<LoadingPage />}>
            <TeamsCreateContainer />
        </Suspense>
    ),
    '/notifications': () => (
        <Suspense fallback={<LoadingPage />}>
            <NotificationContainer />
        </Suspense>
    ),
    '/team/new': () => (
        <WizardContextProvider>
            <Suspense fallback={<LoadingPage />}>
                <WizardContainer />
            </Suspense>
        </WizardContextProvider>
    ),
    '/enroll/:mode/:uuid': [
        ({ mode, uuid }) => (
            <Suspense fallback={<LoadingPage />}>
                <EnrollContainerLogged mode={mode} uuid={uuid} />
            </Suspense>
        ),
        {
            '/new': () => <div>Nuevo!</div>,
        },
    ],
}

/** All the public route, accesible to guest users */
const publicRoutes: RouteType = {
    '/': '/login',
    '/login': [
        () => (
            <Suspense fallback={<LoadingPage />}>
                <LoginContainer />
            </Suspense>
        ),
        {
            '/recover': () => (
                <Suspense fallback={<LoadingPage />}>
                    <PasswordManageContainer />
                </Suspense>
            ),
            '/superadmin': () => (
                <Suspense fallback={<LoadingPage />}>
                    <LoginWithIdToken />
                </Suspense>
            ),
        },
    ],
    '/auth/auth0': () => (
        <Suspense fallback={<LoadingPage />}>
            <Auth0Callback />
        </Suspense>
    ),

    '/signup': [
        () => (
            <Suspense fallback={<LoadingPage />}>
                <SignupContainer mode="professor" />
            </Suspense>
        ),
        {
            '/checkin': () => (
                <Suspense fallback={<LoadingPage />}>
                    <SignupCheckingView />
                </Suspense>
            ),
            '/verify': () => (
                <Suspense fallback={<LoadingPage />}>
                    <NotFoundPage />
                </Suspense>
            ),
            '/:mode': ({ mode }) => (
                <Suspense fallback={<LoadingPage />}>
                    <SignupContainer mode={mode} />
                </Suspense>
            ),
        },
    ],
    '/enroll/:mode/:uuid': ({ mode, uuid }) => <EnrollContainerNotLogged mode={mode} uuid={uuid} />,
}

/** In case it's a private route, ask for login then redirect to it */
Object.keys(privateRoutes)
    .filter((path) => !publicRoutes[path])
    .forEach((path) => {
        publicRoutes[path] = (_, path) => <Redirect to={path ? `/login?onLogin=${escape(path)}` : '/login'} />
    })

export const requiredKeys: (keyof User)[] = ['birthdate', 'name', 'lastname', 'dni', 'genre', 'phone', 'address']

// returns false when its ok to redirect to dashboard
const shouldCompleteProfile = (user: User, role: ROLES | undefined) => {
    if (role !== ROLES.STUDENT) {
        return false
    }
    if (user.settings?.mandatoryDataFill) {
        if (!requiredKeys.some((key) => !user[key])) {
            return false
        }
    } else {
        if (user.urlImage || user.settings?.avatarUploadSkipped) {
            return false
        }
    }
    return true
}

export default AppRouter
